import { Button, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useAccountOrMembershipRequiredModalContext } from '../../Anonymous/AccountOrMembershipRequiredModal/contexts/AccountOrMembershipRequiredModalContext';
import { useUser } from '../../User/containers/UserDataStore';
import { useComments } from '../containers/commentsDataStore';
import CommentInputWithMentioning from './CommentInputWithMentioning/CommentInputWithMentioning';
import CommunityGuidelinesLink from '../../UI/Links/CommunityGuidelines/CommunityGuidelinesLink';

CommentInputFrame.propTypes = {
	parentComment: PropTypes.object,
	comment: PropTypes.object,
	childCount: PropTypes.number,
	communityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	threadCount: PropTypes.number,
	isEditingExistingNote: PropTypes.bool,
	toggleEditMode: PropTypes.func,
};

CommentInputFrame.defaultProps = {
	comment: {},
	isEditingExistingNote: false,
};

export default function CommentInputFrame({
	parentComment,
	comment,
	childCount,
	threadCount,
	communityId,
	isEditingExistingNote,
	toggleEditMode,
}) {
	const [isEditingNote, setIsEditingNote] = React.useState(false);
	const [text, setText] = React.useState(comment.text || '');
	const { setIsVisible } = useAccountOrMembershipRequiredModalContext();
	const { isAnonymous } = useUser();

	useEffect(() => {
		setIsEditingNote(isEditingExistingNote);
	}, [parentComment, childCount, threadCount]);

	const commentsDataStore = useComments();

	const renderInput = (placeholderText, isReply) => {
		let isReadyToSave = text && text !== comment.text;
		const inputClassName =
			isReply || isEditingExistingNote
				? 'comment-input-reply'
				: 'comment-input-new-thought';
		return (
			<div className="comment-frame">
				<CommentInputWithMentioning
					comment={comment && comment.text}
					className={inputClassName}
					placeholderText={placeholderText}
					onChangeEvent={setText}
				/>
				{isReadyToSave ? (
					<Tooltip
						title={
							<span style={{ fontSize: 'smaller' }}>Send</span>
						}>
						<div
							className="comment-save icon-send"
							onClick={saveComment}
						/>
					</Tooltip>
				) : (
					<Tooltip
						title={
							<span style={{ fontSize: 'smaller' }}>Cancel</span>
						}>
						<div
							className="comment-cancel icon-declined"
							onClick={leaveEditMode}
						/>
					</Tooltip>
				)}
			</div>
		);
	};

	const saveComment = () => {
		const isReply = parentComment || false;
		if (isReply) {
			parentComment.text = text;
			parentComment.parentCommentId = parentComment.id;
			commentsDataStore.createNewComment(parentComment, true);
			leaveEditMode();
		} else if (isEditingExistingNote) {
			comment.text = text;
			commentsDataStore.updateComment(comment);
			toggleEditMode();
		} else {
			const newComment = { text: text, parentCommentId: 0, communityId };
			commentsDataStore.createNewComment(newComment);
			leaveEditMode();
			setText('');
		}
	};

	const leaveEditMode = () => {
		setIsEditingNote(false);
		isEditingExistingNote && toggleEditMode();
	};

	const handleGoToEditMode = () => {
		return isAnonymous() ? setIsVisible(true) : setIsEditingNote(true);
	};

	const renderCommentFooter = () => {
		const commentFooterClass = classNames({
			'new-thought-comment-frame': !isEditingExistingNote,
			'new-thought-button-and-guidelines-link':
				!isEditingExistingNote && !isEditingNote,
		});
		return (
			<div className={commentFooterClass}>
				{isEditingNote ? (
					<React.Fragment>
						{renderInput(
							'Type your thought here and use the "@" to link title',
							false,
						)}
					</React.Fragment>
				) : (
					<>
						<div onClick={handleGoToEditMode} className="clickable">
							<Button>New Thought</Button>
						</div>
						<CommunityGuidelinesLink />
					</>
				)}
			</div>
		);
	};

	const renderReplyButton = () => (
		<div onClick={handleGoToEditMode} className="clickable">
			<div className="column icon-export" />
			<div className="column-spaced">Reply</div>
			<div className="clear" />
		</div>
	);

	return (
		<React.Fragment>
			{!parentComment || Number(parentComment.id) === 0 ? (
				renderCommentFooter()
			) : (
				<div className="inline-comment clickable">
					{isEditingNote
						? renderInput('Reply', true)
						: renderReplyButton()}
				</div>
			)}
		</React.Fragment>
	);
}
